import { BasicButton, SecondCustomInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useProfile from "@/hooks/useProfile";
import { postRegister } from "@/redux/reducers/auth";
import { ProfessionalInformationFieldsSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { createFileRoute } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/profile/information",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["settings", "common"]);
	const dispatch = useDispatch();
	const handleOnBack = useBackward();

	const { profile: userData } = useProfile();

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<ProfessionalInformationFieldsSchema>({
		mode: "all",
		resolver: zodResolver(ProfessionalInformationFieldsSchema),
		defaultValues: async () => ({
			job_title: userData?.job_title ?? "",
			qualifications: userData?.qualification ?? "",
			specialization: userData?.spesialization ?? "",
			directory: userData?.directory ?? false,
		}),
	});

	const onSubmit: SubmitHandler<ProfessionalInformationFieldsSchema> = async (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		return dispatch(
			postRegister({
				job_title: data.job_title,
				spesialization: data.specialization,
				qualification: data.qualifications,
				directory: data.directory,
				callback: () => {
					handleOnBack();
					toast.success(t("settings:profile.professionalInfoUpdated"));
				},
			}),
		);
	};

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						{t("settings:profile.additionalInformationHeading")}
					</Typography>
					<Controller
						control={control}
						name="job_title"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								{...field}
								type="text"
								label={t("settings:profile.jobTitle")}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
					<Controller
						control={control}
						name="qualifications"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								{...field}
								type="text"
								label={t("settings:profile.qualifications")}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
					<Controller
						control={control}
						name="specialization"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								{...field}
								type="text"
								label={t("settings:profile.specialisation")}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
					<Typography fontWeight={700} fontSize={14}>
						{t("settings:profile.addToDirectory")}
					</Typography>
					<Controller
						control={control}
						name="directory"
						defaultValue={false}
						render={({ field }) => (
							<Stack
								direction="row"
								spacing={2}
								justifyContent="center"
								alignItems="center"
							>
								<BasicButton
									variant="outlined"
									color={field.value === true ? "info" : "inherit"}
									className="w-full !h-12 !text-base font-medium !shadow-none"
									onClick={() => field.onChange(true)}
									onBlur={field.onBlur}
								>
									<Box className="flex items-center gap-2">
										<CheckIcon />
										<span>{t("settings:profile.yes")}</span>
									</Box>
								</BasicButton>
								<BasicButton
									variant="outlined"
									color={field.value === false ? "error" : "inherit"}
									className="w-full !h-12 !text-base font-medium !shadow-none"
									onClick={() => field.onChange(false)}
									onBlur={field.onBlur}
								>
									<Box className="flex items-center gap-2">
										<CloseIcon />
										<span>{t("settings:profile.no")}</span>
									</Box>
								</BasicButton>
							</Stack>
						)}
					/>
					<Box className="flex items-start gap-1">
						<InfoRoundedIcon className="!text-base" />
						<p className="text-dark text-xs font-medium">
							{t("settings:profile.directoryNotAddedHint")}
						</p>
					</Box>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disabled={!isDirty || !isValid || isSubmitting}
							loading={isSubmitting}
						>
							{t("common:save")}
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
